/**
 * Script alphabets — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_lettering`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const scriptAlphabetsPack: ToilePack = {
    slug: 'script-alphabets',
    category: 'illustration',
    name: 'Script alphabets',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'modern', label: 'Modern script', subject: 'a chart of a modern bouncy script, capitals "A" to "Z" and lowercase "a" to "z", letters dipping below and above the baseline'},
        {key: 'formal', label: 'Formal script', subject: 'a chart of a formal pointed-pen script, capitals "A" to "Z" with oval loops and swelling shades, on a consistent slant'},
        {key: 'casual', label: 'Casual script', subject: 'a chart of a casual brush script lowercase "a" to "z" in four rows, rounded letters with thick downstrokes'},
    ],
    styles: [
        {key: 'solid', label: 'Solid black', style: 'solid black joined letterforms with smooth contrast between thick and thin strokes', palette: 'black ink only', framing: 'the chart filling the page inside an even white margin'},
        {key: 'monoline', label: 'Monoline', style: 'joined letterforms in one even monoline stroke', palette: 'black ink only', framing: 'the chart filling the page inside an even white margin'},
        {key: 'guides', label: 'With guides', style: 'solid black joined letterforms on thin baseline, x-height and slant guide lines', palette: 'black ink only', framing: 'the chart filling the page inside an even white margin'},
    ],
    directions: ['numbers', 'a name', 'with swashes'],
    directionHint: 'try: numbers',
    seo: {
        title: 'Script Alphabets: Modern, Formal, Casual Brush',
        metaDescription: 'Nine script alphabet sheets: modern bouncy, formal pointed-pen and casual brush script, in black on white.',
        h1: 'Script alphabets: modern bouncy, formal pointed-pen, casual brush',
        tagline: 'Joined script alphabets in three hands, on reference sheets.',
        intro: 'Script alphabets are joined letters for wedding signs, cards and lettering projects. The nine sheets chart three hands: a modern bouncy script with letters dipping below the line, a formal pointed-pen script with oval loops and swelling shades, and a casual brush script of rounded lowercase with thick downstrokes. Each hand is shown in solid black, in one even monoline, or with baseline and slant guides. Ask for numbers, a name or swashes.',
        useCases: [
            'a wedding sign',
            'a card or an invitation',
            'a lettering project for a class',
            'a reference for a sign painter',
        ],
        faq: [
            {q: 'What is the difference between the three hands?', a: 'Modern is bouncy, with letters dipping below the line. Formal is a pointed-pen script with oval loops. Casual is a brush script with rounded lowercase and thick downstrokes.'},
            {q: 'What is a monoline script?', a: 'The same hands drawn with one even line weight, with no thick and thin strokes.'},
            {q: 'Which cases are shown?', a: 'The modern sheet has capitals and lowercase, the formal sheet has capitals, and the casual sheet has lowercase.'},
            {q: 'What do the guides show?', a: 'The guided sheet shows baseline, x-height and slant lines, so the joins sit on the right lines.'},
        ],
    },
}
